import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { championIconUrl } from "../config/constants";
import { CHAMPIONS, TIER_COLORS, type ChampionId } from "../data/champions";
import type { MapNode } from "../map/CampaignMap";
import { MapScene } from "./MapScene";
import { recruitStartXp } from "./NodeRouter";
import { levelForXp } from "../units/UnitStats";
import { showChampionTooltip, hideChampionTooltip } from "../ui/ChampionTooltip";

export class RecruitPickScene implements Scene {
  private root: HTMLDivElement;

  constructor(
    private game: Game,
    private node: MapNode,
    private offers: ChampionId[],
  ) {
    this.root = document.createElement("div");
    this.root.className = "choice-screen";
  }

  enter(): void {
    this.renderOfferStep();
    this.game.sceneRoot.appendChild(this.root);
  }

  exit(): void {
    hideChampionTooltip();
    this.root.remove();
  }

  update(_dt: number): void {}
  render(_ctx: CanvasRenderingContext2D): void {}

  private renderOfferStep(): void {
    const state = this.game.runState;
    if (!state) return;
    const isFull = state.isRosterFull();

    this.root.innerHTML = `
      <div class="reward-title">RECRUIT</div>
      <div style="color:var(--text-dim);font-size:11px;letter-spacing:2px;margin-bottom:8px">
        ${isFull ? "ROSTER FULL — PICK A REPLACEMENT" : "CHOOSE A CHAMPION"}
      </div>
      <button class="btn ghost btn-sm" id="skip-btn">SKIP</button>
      <div class="choice-card-list" id="offers"></div>
    `;
    this.root.style.paddingBottom = "calc(var(--ad-anchor-height) + 100px)";
    const list = this.root.querySelector("#offers") as HTMLDivElement;

    if (this.offers.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText = "color:var(--text-dim);font-size:12px";
      empty.textContent = "No new champions available.";
      list.appendChild(empty);
    }

    const startXp = recruitStartXp(this.game, this.node);
    const recruitLevel = levelForXp(startXp);

    for (const id of this.offers) {
      const def = CHAMPIONS[id];
      const tierColor = TIER_COLORS[def.tier];
      const ownedCopies = state.roster.filter(r => r.championId === id);
      const bestOwned = ownedCopies.reduce<typeof ownedCopies[0] | undefined>(
        (best, r) => (!best || r.stars > best.stars ? r : best), undefined
      );
      const willMerge = bestOwned && bestOwned.stars < 3;

      let subtitle = "";
      if (willMerge) {
        const nextStars = (bestOwned!.stars + 1);
        subtitle = `<span style="color:#f0c040;font-size:10px">${"★".repeat(bestOwned!.stars)} → ${"★".repeat(nextStars)} MERGE</span>`;
      } else if (bestOwned) {
        subtitle = `<span style="color:var(--text-dim);font-size:10px">already at max ★★★</span>`;
      }

      const wrapper = document.createElement("div");
      wrapper.className = "recruit-card-wrap";

      const card = document.createElement("div");
      card.className = "choice-card cs-simple";
      card.style.setProperty("--tier", tierColor);
      card.innerHTML = `
        <div class="icon" style="background-image:url('${championIconUrl(def.iconId)}')"></div>
        <div class="title">${def.displayName} <span style="color:var(--text-dim);font-size:10px">Lv.${recruitLevel}</span>${subtitle ? `<br>${subtitle}` : ""}</div>
      `;
      card.addEventListener("click", () => {
        if (state.isRosterFull() && !willMerge) {
          this.renderReplaceStep(id, startXp);
          return;
        }
        state.addChampion(id, startXp, willMerge);
        state.tryMerge(id);
        this.finish();
      });
      wrapper.appendChild(card);

      const infoBtn = document.createElement("button");
      infoBtn.type = "button";
      infoBtn.className = "recruit-info-btn";
      infoBtn.textContent = "ⓘ  Info";
      const showTip = () => showChampionTooltip(id, recruitLevel, infoBtn);
      infoBtn.addEventListener("click", showTip);
      infoBtn.addEventListener("mouseenter", showTip);
      infoBtn.addEventListener("mouseleave", () => hideChampionTooltip());
      wrapper.appendChild(infoBtn);

      list.appendChild(wrapper);
    }

    (this.root.querySelector("#skip-btn") as HTMLButtonElement).addEventListener(
      "click",
      () => this.finish(),
    );

    this.appendTeamBar();
  }

  private renderReplaceStep(newId: ChampionId, startXp: number): void {
    const state = this.game.runState;
    if (!state) return;
    const newDef = CHAMPIONS[newId];
    const newColor = TIER_COLORS[newDef.tier];

    this.root.innerHTML = `
      <div class="reward-title">RELEASE A CHAMPION</div>
      <div style="color:var(--text-dim);font-size:11px;letter-spacing:1px;margin-bottom:10px">
        INCOMING: <span style="color:${newColor}">${newDef.displayName}</span>
      </div>
      <div class="choice-card-list" id="release-cards"></div>
      <button class="btn ghost btn-sm" id="cancel-btn">CANCEL</button>
    `;
    this.root.style.paddingBottom = "20px";
    const list = this.root.querySelector("#release-cards") as HTMLDivElement;

    for (const entry of state.roster) {
      const def = CHAMPIONS[entry.championId];
      const tierColor = TIER_COLORS[def.tier];
      const level = levelForXp(entry.xp);
      const card = document.createElement("div");
      card.className = "choice-card cs-simple";
      card.style.setProperty("--tier", tierColor);
      card.innerHTML = `
        <div class="icon" style="background-image:url('${championIconUrl(def.iconId)}')"></div>
        <div class="title">${def.displayName} <span style="color:var(--text-dim);font-size:10px">Lv.${level}</span></div>
      `;
      const entryId = entry.id;
      card.addEventListener("click", () => {
        const idx = state.roster.findIndex((r) => r.id === entryId);
        if (idx === -1) return;
        state.roster.splice(idx, 1);
        state.addChampion(newId, startXp);
        state.tryMerge(newId);
        this.finish();
      });
      list.appendChild(card);
    }

    (this.root.querySelector("#cancel-btn") as HTMLButtonElement).addEventListener(
      "click",
      () => this.renderOfferStep(),
    );
  }

  private appendTeamBar(): void {
    const state = this.game.runState;
    if (!state) return;
    const teamBar = document.createElement("div");
    teamBar.className = "recruit-team-bar";
    const label = document.createElement("div");
    label.className = "recruit-team-label";
    label.textContent = "YOUR TEAM";
    teamBar.appendChild(label);
    const icons = document.createElement("div");
    icons.className = "recruit-team-icons";
    for (const entry of state.roster) {
      const def = CHAMPIONS[entry.championId];
      const level = levelForXp(entry.xp);
      const slot = document.createElement("div");
      slot.className = "recruit-team-slot";
      const stars = entry.stars ?? 1;
      slot.title = `${def.displayName} ${"★".repeat(stars)} Lv.${level}`;
      slot.innerHTML = `
        <div class="recruit-team-icon" style="background-image:url('${championIconUrl(def.iconId)}')"></div>
        <div class="recruit-team-level">Lv${level}${stars > 1 ? " " + "★".repeat(stars) : ""}</div>
      `;
      icons.appendChild(slot);
    }
    teamBar.appendChild(icons);
    this.root.appendChild(teamBar);
  }

  private finish(): void {
    const state = this.game.runState;
    if (!state) return;
    state.recruitsCompleted++;
    state.pendingRecruitOffers = null;
    this.game.achievements.onRecruit(state.roster);
    state.map?.markComplete(this.node.id);
    this.game.sceneManager.transition(new MapScene(this.game));
  }
}
